// client-court.ts — HTTP client cho module Court (@riptik/court-backoffice-module). Mirror
// client-fnbpos.ts VỀ MẶT NGUYÊN TẮC (dùng chung getAuthToken/getBaseUrl từ './client', KHÔNG lặp
// logic token) nhưng KHÁC cách tổ chức: court-provider-service không có openapi.json sinh sẵn (khác
// fnbpos), nên module này gọi thẳng qua bo-gateway proxy bằng fetch thủ công, không qua codegen client.
//
// Backend nguồn sự thật: bo-gateway (internal/handlers/court) reverse-proxy
// `/organizers/:organizer/court/:supplierId/*path` -> court-provider-service (JSON, mọi method
// downstream là POST — request() bên dưới vẫn cho phép override method cho các route PUT/DELETE thật sự
// tồn tại phía gateway), cộng `GET /organizers/:organizer/court/venues` để liệt kê venue (không bao giờ
// trả api_key — xem CourtContext.tsx).
import { getAuthToken, getBaseUrl } from './client';
import { OrdersService, ThirdpartyService, thirdparty_ProductCreateRequest } from './generated';
import type { thirdparty_EventProductAssignmentCreateRequest } from './generated';
import type { BookingCreateInput } from '@riptik/court-backoffice-module';

// Court-provider-service và bo-gateway's court handler trả JSON TRẦN (mảng/object trực tiếp là payload,
// KHÔNG bọc {success, data} như các service khác trong repo này) — bo-gateway chỉ bọc {success:false,
// error} cho CHÍNH lỗi của nó (invalid_organizer, venue_not_found, upstream_unavailable...), còn lỗi từ
// court-provider-service pass-through nguyên trạng thường có dạng {error: "..."}. doFetch vì vậy KHÔNG
// được unwrap `.data` — trả thẳng body đã parse làm payload, chỉ đọc field lỗi khi res.ok === false.
interface ApiErrorBody {
  success?: false;
  error?: string;
  message?: string;
}

/**
 * court-provider-service trả JSON snake_case (`resource_id`, `open_time`...) — TOÀN BỘ type trong
 * `@riptik/court-backoffice-module` (Court/PriceRule/BookingBlock) lại định nghĩa camelCase
 * (`resourceId`, `openTime`...), KHÔNG có lớp convert nào ở giữa cho tới bug này. Hậu quả thật: mọi
 * field bị `undefined` ở runtime (TS `as T` chỉ ép kiểu, không đổi dữ liệu) — vd `record.resourceId`
 * luôn undefined -> link "Khung giá" điều hướng sang `.../undefined/pricing` -> tạo giá gửi
 * `resource_id: "undefined"` lên backend -> vi phạm FK `court_price_rules_venue_resource_fkey`. Sửa
 * tận gốc bằng 1 bước convert đệ quy áp dụng cho MỌI response — venues (đã camelCase sẵn từ bo-gateway,
 * xem `listVenues`) đi qua hàm này vẫn an toàn vì convert 1 field không có `_` là no-op.
 */
function snakeToCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_match, c: string) => c.toUpperCase());
}

function deepCamelize<T>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((v) => deepCamelize(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[snakeToCamelKey(k)] = deepCamelize(v);
    }
    return out as T;
  }
  return value as T;
}

// --- Staff session (xem plan "Staff/Phân quyền cho module Court" §1.6) — sessionStorage theo supplierId,
// KHÔNG localStorage (tự xoá khi đóng tab, giảm rủi ro token bị bỏ quên trên máy quầy dùng chung). Host
// (CourtProvider.tsx) SỞ HỮU việc lưu — module chỉ gọi verifyPin/hasValidSession/clearSession qua Context,
// KHÔNG tự biết cơ chế lưu trữ (đúng nguyên tắc DIP, xem CourtContext.tsx).
interface CourtStaffSession {
  token: string;
  name: string;
  roles: string[];
  expiresAt: number; // epoch ms — client tự theo dõi song song với exp thật trong JWT, tránh phải decode JWT ở FE.
}

function staffSessionKey(supplierId: number | string): string {
  return `court_staff_session:${supplierId}`;
}

function getStaffSession(supplierId: number | string): CourtStaffSession | null {
  try {
    const raw = sessionStorage.getItem(staffSessionKey(supplierId));
    if (!raw) return null;
    const session = JSON.parse(raw) as CourtStaffSession;
    if (!session.expiresAt || session.expiresAt <= Date.now()) return null;
    return session;
  } catch {
    return null;
  }
}

function setStaffSession(supplierId: number | string, session: CourtStaffSession): void {
  sessionStorage.setItem(staffSessionKey(supplierId), JSON.stringify(session));
}

export function clearStaffSession(supplierId: number | string): void {
  sessionStorage.removeItem(staffSessionKey(supplierId));
}

export function hasValidStaffSession(supplierId: number | string): boolean {
  return getStaffSession(supplierId) !== null;
}

// courtStaffTokenTTLMs — mirror TTL 12h ký ở bo-gateway (staff.go, CourtStaffClaims) — client tự theo dõi
// song song, KHÔNG decode JWT thật (đơn giản hơn, đủ dùng vì server luôn là nguồn xác thực cuối cùng —
// client-side expiry chỉ để chủ động hỏi lại PIN sớm, không phải cơ chế bảo mật).
const courtStaffTokenTTLMs = 12 * 60 * 60 * 1000;

// Mã lỗi kỹ thuật của court-provider / booking-core → câu tiếng Việt cho nhân viên. Thay theo từ khoá nên cũng
// dịch được lỗi bọc nhiều tầng (vd "khung mới: slot_unavailable: ..."); phần chi tiết sau mã giữ nguyên.
const COURT_ERROR_VI: Array<[string, string]> = [
  ['outside_opening_hours', 'Ngoài giờ mở cửa của sân'],
  ['nothing_to_cut', 'Chưa có phần thời gian nào để cắt'],
  ['slot_unavailable', 'Khung giờ đã có người đặt'],
  ['time_range_not_covered', 'Khung giờ nằm ngoài lịch mở của sân'],
  ['court_closed', 'Sân đóng cửa hoặc đang bảo trì trong khung giờ này'],
  ['invalid_duration', 'Thời lượng không hợp lệ (không đúng bước đặt tối thiểu)'],
  ['resource_not_found', 'Không tìm thấy sân'],
  ['resource_inactive', 'Sân đang ngừng hoạt động'],
  ['booking_not_found', 'Không tìm thấy lượt đặt'],
  ['price_rule_conflict', 'Khung giá bị chồng lấn'],
  ['too_many_occurrences', 'Gói định kỳ có quá nhiều buổi'],
  ['invalid_pattern', 'Lịch định kỳ không hợp lệ'],
  ['unauthorized', 'Không có quyền thực hiện thao tác này'],
  ['staff_auth_failed', 'Mã PIN không đúng'],
  ['shift_already_open', 'Ca làm việc đang mở'],
  ['no_open_shift', 'Chưa mở ca làm việc'],
  ['pin_already_used', 'Mã PIN này đã được dùng'],
  ['invalid_role', 'Vai trò không hợp lệ'],
  ['order is not awaiting payment', 'Đơn không còn ở trạng thái chờ thanh toán'],
  ['amount must be positive', 'Số tiền phải lớn hơn 0'],
  ['order not found', 'Không tìm thấy đơn'],
];

export function translateCourtError(raw: string): string {
  let out = raw;
  for (const [code, vi] of COURT_ERROR_VI) {
    if (out.includes(code)) out = out.split(code).join(vi);
  }
  return out;
}

async function doFetch<T>(url: string, method: string, body?: Record<string, unknown>, extraHeaders?: Record<string, string>): Promise<T> {
  const token = getAuthToken();
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...extraHeaders,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  // Phòng hờ body rỗng (dù DELETE deactivate/reactivate thực tế trả 200 kèm JSON, không phải 204 —
  // xác nhận qua review — vẫn giữ nhánh này cho an toàn nếu có endpoint khác trả rỗng thật).
  const text = await res.text();
  const json = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const errBody = json as ApiErrorBody | undefined;
    throw new Error(translateCourtError(errBody?.error || errBody?.message || `court request failed (${res.status})`));
  }
  return deepCamelize<T>(json);
}

/**
 * Escape hatch tổng quát — gọi bất kỳ route nào trong court-provider-service qua proxy bo-gateway.
 * `path` = phần route SAU `/court/:supplierId`, vd '/courts', '/price-rules/42'.
 *
 * Chuẩn hoá `path` LUÔN có `/` đầu trước khi ghép URL — mọi call site trong
 * `@riptik/court-backoffice-module` (CourtsPage/CourtPricingPage/BookingsPage) đều gọi với path KHÔNG
 * có `/` đầu (vd 'courts', 'search') thay vì '/courts'. Thiếu bước chuẩn hoá này URL ghép ra dính liền
 * ".../court/135courts" — sai path, không match được route Proxy phía bo-gateway (bug thật phát hiện
 * qua production, ảnh hưởng TOÀN BỘ request của module này, không phải 1 chỗ lẻ).
 */
export async function request<T = unknown>(
  organizationSlug: string,
  supplierId: number | string,
  path: string,
  body?: Record<string, unknown>,
  method = 'POST'
): Promise<T> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/court/${encodeURIComponent(
    String(supplierId)
  )}${normalizedPath}`;
  // Gắn staff token nếu ĐANG có session hợp lệ cho ĐÚNG venue này (xem plan §1.6) — bo-gateway
  // (RequireCourtStaffAuth, "gate mềm") chỉ kiểm tra khi header này có mặt, request không mang header vẫn
  // hợp lệ như trước (venue owner/org_admin qua JWT backoffice thường).
  const session = getStaffSession(supplierId);
  const extraHeaders = session ? { 'X-Court-Staff-Token': session.token } : undefined;
  return doFetch<T>(url, method, body, extraHeaders);
}

interface StaffPinResponse {
  success: boolean;
  token: string;
  staff_id: string;
  name: string;
  roles: string[];
}

/**
 * staffPinVerify — POST /organizers/:organizer/court/staff/pin/verify?supplier_id=. Route LITERAL
 * SIBLING (KHÔNG qua `request()`/wildcard Proxy — cùng lý do đã ghi ở `resolveResourceItem`: node
 * `:supplierId` đã có child wildcard `*path`, gin panic nếu thêm child tĩnh khác). Lưu session (token +
 * refresh_token) vào sessionStorage NGAY khi thành công — mọi `request()` gọi SAU đó tự động gắn header.
 */
export async function staffPinVerify(
  organizationSlug: string,
  supplierId: number | string,
  pin: string
): Promise<{ name: string; roles: string[] }> {
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/court/staff/pin/verify?supplier_id=${encodeURIComponent(String(supplierId))}`;
  const resp = await doFetch<StaffPinResponse>(url, 'POST', { pin });
  setStaffSession(supplierId, {
    token: resp.token,
    name: resp.name,
    roles: resp.roles,
    expiresAt: Date.now() + courtStaffTokenTTLMs,
  });
  return { name: resp.name, roles: resp.roles };
}

/** Liệt kê venue cấp organizer (KHÔNG bao giờ chứa api_key, xem CourtContext.tsx). */
export async function listVenues(
  organizationSlug: string
): Promise<Array<{ supplierId: number; name: string; eventSlug: string }>> {
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/court/venues`;
  return doFetch(url, 'GET');
}

/**
 * Orchestration "Thêm sân mới" (3 bước, quyết định trong plan đã duyệt — KHÔNG có 2-phase-commit/
 * rollback ở v1, chấp nhận "orphan" nếu 1 bước fail giữa chừng vì `external_product_id` unique nên
 * retry an toàn — gọi lại từ đầu không tạo trùng court hay trùng product):
 *
 * 1. POST court-provider-service qua proxy -> tạo court, trả về resource_id.
 * 2. POST /organizers/:organizer/thirdparty/products (KHÔNG qua court proxy, endpoint bo-gateway có
 *    sẵn) -> tạo thirdparty product với product_category="court", external_product_id=resource_id.
 * 3. POST /organizers/:organizer/events/:event/thirdparty/products -> gán product vào event
 *    (eventSlug lấy từ venue, xem CourtContextValue.venues[].eventSlug).
 *
 * Mỗi bước fail thì throw thẳng (không swallow) — UI hiện lỗi rõ bước nào fail, user tự retry.
 */
export async function createCourtWithProduct(
  organizationSlug: string,
  supplierId: number,
  eventSlug: string,
  courtInput: Record<string, unknown>
): Promise<void> {
  // Bước 1 — tạo court trong court-provider-service qua proxy.
  const court = await request<{ resource_id?: string; resourceId?: string }>(
    organizationSlug,
    supplierId,
    '/courts',
    courtInput,
    'POST'
  );
  const resourceId = court.resource_id ?? court.resourceId;
  if (!resourceId) {
    throw new Error('court-provider-service không trả về resource_id sau khi tạo court');
  }

  // Bước 2 — tạo thirdparty product org-level, external_product_id = resourceId (khoá idempotent khi
  // retry). product_name lấy từ courtInput.name nếu có, fallback resourceId.
  const productName =
    (typeof courtInput.name === 'string' && courtInput.name) || `Court ${resourceId}`;
  const productReq: thirdparty_ProductCreateRequest = {
    supplier_id: supplierId,
    external_product_id: resourceId,
    product_category: thirdparty_ProductCreateRequest.product_category.COURT,
    product_name: productName,
    inventory_mode: thirdparty_ProductCreateRequest.inventory_mode.UNLIMITED,
    fulfillment_type: thirdparty_ProductCreateRequest.fulfillment_type.INSTANT,
    delivery_method: thirdparty_ProductCreateRequest.delivery_method.MANUAL,
    active: true,
  };
  const product = await ThirdpartyService.postOrganizersThirdpartyProducts(organizationSlug, productReq);
  if (!product.id) {
    throw new Error('thirdparty product created nhưng không có id trong response');
  }

  // Bước 3 — gán product vào event. tax_rate bắt buộc phía backend (mantik-tax-config.md); court là
  // dịch vụ đặt sân, mặc định "0" (miễn thuế) — tenant chỉnh lại qua trang thirdparty product nếu cần,
  // không phải phạm vi form "Thêm sân" này.
  const assignReq: thirdparty_EventProductAssignmentCreateRequest = {
    product_id: product.id,
    tax_rate: '0',
  };
  await ThirdpartyService.postOrganizersEventsThirdpartyProducts(organizationSlug, eventSlug, assignReq);
}

// ResolveResourceItemData — CAMELCASE (khớp field thật SAU khi qua deepCamelize trong doFetch(), KHÔNG
// phải shape snake_case thô bo-gateway trả về). Bug thật đã sửa: khai báo snake_case trước đó khiến
// destructure ở createBooking() luôn đọc `undefined` (deepCamelize áp dụng cho MỌI response qua
// doFetch, xem comment đầu file) — mọi booking tạo ra gửi `item_id: "undefined"` lên order-create.
interface ResolveResourceItemData {
  itemId: number;
  eventId: number;
  eventSlug: string;
}

/**
 * Tra item_id/event tương ứng với 1 sân cụ thể — bo-gateway's
 * GET .../court/resource-item?supplier_id=&resource_id= (lookup mới, xem plan "đặt sân hộ khách").
 * KHÔNG đi qua `request()` ở trên (route này là sibling literal của "venues", không lồng dưới
 * :supplierId như phần lớn route court khác — tránh xung đột với route wildcard Proxy phía bo-gateway).
 */
async function resolveResourceItem(
  organizationSlug: string,
  supplierId: number,
  resourceId: string
): Promise<ResolveResourceItemData> {
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/court/resource-item?supplier_id=${encodeURIComponent(
    String(supplierId)
  )}&resource_id=${encodeURIComponent(resourceId)}`;
  const resp = await doFetch<{ success: boolean; data: ResolveResourceItemData }>(url, 'GET');
  return resp.data;
}

/**
 * Orchestration "Đặt sân hộ khách" (đơn lẻ | định kỳ) — xem plan "copper-lantern-recurring-court":
 * 1. Tra item_id/event từ resource_id (module chỉ biết resource_id, order-create cần item_id).
 * 2. Build reservation_params đúng shape booking-core mong đợi (đơn lẻ: resource_id+start_at+end_at;
 *    định kỳ: resource_id+weekday+start_time+end_time+start_date+end_date — weekday convert sang
 *    string, KHÔNG gửi số nguyên trần vì reservation_params là map[string]string).
 * 3. Gọi order-create thật (OrdersService, đã có reservation_params sau khi bo-gateway/openapi cập
 *    nhật) — 1 position duy nhất, quantity=1 (đúng ngữ nghĩa "1 buổi"/"1 gói định kỳ" = 1 vị trí).
 *
 * Email cấp ĐƠN HÀNG (models_CreateOrderRequest.email, required) dùng CHUNG với attendee_email của
 * position — hợp lý cho ngữ cảnh 1 khách/1 booking (khác vé sự kiện có thể nhiều attendee khác nhau).
 */
export async function createBooking(
  organizationSlug: string,
  supplierId: number,
  eventSlug: string,
  input: BookingCreateInput
): Promise<{ orderCode: string }> {
  const { itemId, eventSlug: resolvedEventSlug } = await resolveResourceItem(
    organizationSlug,
    supplierId,
    input.resourceId
  );

  const reservationParams: Record<string, string> =
    input.mode === 'single'
      ? { resource_id: input.resourceId, start_at: input.startAt, end_at: input.endAt }
      : {
          resource_id: input.resourceId,
          weekday: String(input.weekday),
          start_time: input.startTime,
          end_time: input.endTime,
          start_date: input.startDate,
          end_date: input.endDate,
        };

  const order = await OrdersService.postOrganizersEventsOrders(organizationSlug, resolvedEventSlug || eventSlug, {
    email: input.attendeeEmail,
    locale: 'vi',
    positions: [
      {
        item_id: String(itemId),
        quantity: 1,
        attendee_name: input.attendeeName,
        attendee_email: input.attendeeEmail,
        reservation_params: reservationParams,
      },
    ],
  });

  if (!order.code) {
    throw new Error('Order created nhưng không có code trong response');
  }
  return { orderCode: order.code };
}

/**
 * Đánh dấu order (vừa tạo qua createBooking) đã thu tiền — dùng cho ngữ cảnh khách trả tiền mặt tại
 * quầy/qua điện thoại lúc nhân viên tạo booking hộ, không qua cổng thanh toán online.
 *
 * CHƯA có role gate riêng ở endpoint mark_paid (bo-gateway) — bất kỳ nhân viên có quyền truy cập
 * organizer đều gọi được hành động này hiện tại. Bổ sung phân quyền cụ thể (vd chỉ role
 * cashier/manager) là việc làm SAU (xem plan "copper-lantern-recurring-court" Phần 0.4), không chặn
 * việc bật nút này — quyết định có chủ đích: ưu tiên có tính năng, siết quyền hạn bổ sung sau.
 */
export async function markBookingPaid(organizationSlug: string, eventSlug: string, orderCode: string): Promise<void> {
  await OrdersService.postOrganizersEventsOrdersMarkPaid(organizationSlug, eventSlug, orderCode, {
    send_email: true,
  });
}

// --- Gói đặt sân định kỳ ("1 order, nhiều payment, thu dần") — order thuộc gói này bị bo-gateway/
// booking-core CHẶN gọi markBookingPaid ở trên (mark cả đơn sai — chỉ được ghi nhận đúng kỳ đang chờ).
// 2 hàm dưới gọi THẲNG route mới ở bo-gateway (chưa có trong generated OrdersService — route quá mới, chưa
// regen openapi client), dùng lại `doFetch`/`getBaseUrl`/`getAuthToken` sẵn có trong file này thay vì lặp
// logic fetch.

export interface RecurringPendingPeriod {
  isRecurring: boolean;
  periodId?: number;
  periodIndex?: number;
  amountMinor?: number;
}

/**
 * getRecurringPendingPeriod — order này có thuộc gói đặt sân định kỳ không, nếu có thì kỳ nào đang chờ trả
 * bao nhiêu (KHÔNG BAO GIỜ dùng order.total của order thuộc gói — đó là tổng CẢ GÓI).
 */
export async function getRecurringPendingPeriod(
  organizationSlug: string,
  eventSlug: string,
  orderCode: string
): Promise<RecurringPendingPeriod> {
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/events/${encodeURIComponent(
    eventSlug
  )}/orders/${encodeURIComponent(orderCode)}/recurring_pending_period`;
  return doFetch<RecurringPendingPeriod>(url, 'GET');
}

/**
 * recordRecurringPackagePayment — staff xác nhận đã thu tiền mặt cho ĐÚNG kỳ đang chờ của order thuộc gói
 * định kỳ (bo-gateway tự resolve kỳ nào, KHÔNG tin client chọn — xem GetRecurringPendingPeriod ở Go).
 */
export async function recordRecurringPackagePayment(organizationSlug: string, eventSlug: string, orderCode: string): Promise<void> {
  const url = `${getBaseUrl()}/organizers/${encodeURIComponent(organizationSlug)}/events/${encodeURIComponent(
    eventSlug
  )}/orders/${encodeURIComponent(orderCode)}/record_recurring_payment`;
  await doFetch<{ success: boolean }>(url, 'POST', {});
}
